import { unwrapAnalysisPayload } from './utils';

const EXCLUDED_TOP_LEVEL_KEYS = new Set([
  'error',
  'raw_response',
  'token_stats',
  'meta',
  'user_id',
  'video_name',
  'video_name_original',
  'original_search',
  'content',
  'metadata',
  'thumbnail_url',
  'filesize_bytes',
  'duration_seconds',
  'created',
  'updated',
  'timestamp',
  'video_url',
  'blob_path',
  'file_name',
  'file_source',
  'cost_optimization',
  'video_info',
  'analysis_data',
  'processing',
]);

export const parseMetricNumber = (raw: unknown): number => {
  if (typeof raw === 'number' && !Number.isNaN(raw)) {
    return raw;
  }
  if (typeof raw === 'string') {
    const parsed = parseFloat(raw);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

export function normalizeAnalysisData(raw: unknown): Record<string, any> {
  if (!raw) {
    return {};
  }

  let value = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return {};
    }
  }

  return unwrapAnalysisPayload(value);
}

export function countFilledAnalysisFields(analysis: Record<string, any>): number {
  let total = 0;

  const visit = (node: unknown): void => {
    if (node === null || node === undefined || node === '') {
      return;
    }
    if (Array.isArray(node)) {
      node.forEach(visit);
      return;
    }
    if (typeof node === 'object') {
      Object.values(node as Record<string, unknown>).forEach(visit);
      return;
    }
    total += 1;
  };

  for (const [key, value] of Object.entries(analysis)) {
    if (EXCLUDED_TOP_LEVEL_KEYS.has(key)) {
      continue;
    }
    visit(value);
  }

  return total;
}

const PERSON_COUNT_KEY =
  /person_count|people_count|face_count|unique_faces|persons_detected|num_people|on_screen_people/i;

function deepExtractPersonCount(analysis: Record<string, any>): number {
  let maxFound = 0;

  const walk = (node: unknown, keyPath = ''): void => {
    if (typeof node === 'number' && node > 0 && PERSON_COUNT_KEY.test(keyPath)) {
      maxFound = Math.max(maxFound, Math.floor(node));
      return;
    }
    if (Array.isArray(node)) {
      node.forEach((item) => walk(item, keyPath));
      return;
    }
    if (node && typeof node === 'object') {
      for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
        const nextPath = keyPath ? `${keyPath}.${key}` : key;
        walk(value, nextPath);
      }
    }
  };

  walk(analysis);
  return maxFound;
}

function inferPersonsFromHumanSections(analysis: Record<string, any>): number {
  let maxInSection = 0;

  for (const [key, section] of Object.entries(analysis)) {
    if (!key.startsWith('Human_') || key.endsWith('_Recommendations')) {
      continue;
    }
    if (!section || typeof section !== 'object' || Array.isArray(section)) {
      continue;
    }

    const filledCount = Object.values(section as Record<string, unknown>).filter(
      (value) =>
        value !== null &&
        value !== undefined &&
        value !== '' &&
        value !== false &&
        !(Array.isArray(value) && value.length === 0),
    ).length;

    if (filledCount > 0) {
      maxInSection = Math.max(maxInSection, 1);
    }
  }

  return maxInSection;
}

export function extractPersonsDetected(analysisData: unknown): number {
  const analysis = normalizeAnalysisData(analysisData);

  const raw =
    analysis.Human_Demographics?.person_count_estimate ??
    analysis.Human_Demographics?.total_people ??
    analysis.Human_Demographics?.person_count_exact ??
    analysis.Human_Demographics?.face_count_unique ??
    analysis.Scene_Object_Detection?.person_count ??
    analysis.personas?.face_count_unique ??
    analysis.demographics?.person_count ??
    analysis.Meta_Information?.person_count ??
    0;

  const explicit = parseMetricNumber(raw);
  if (explicit > 0) {
    return explicit;
  }

  const deep = deepExtractPersonCount(analysis);
  if (deep > 0) {
    return deep;
  }

  return inferPersonsFromHumanSections(analysis);
}

export function buildDashboardMetricsForDb(analysisData: unknown): {
  total_persons_detected: number;
  total_features_processed: number;
  feature_total_count: number;
} {
  const total_persons_detected = extractPersonsDetected(analysisData);
  const total_features_processed = extractFeaturesProcessed(analysisData);
  const analysis = normalizeAnalysisData(analysisData);
  const metaCount = parseMetricNumber(
    analysis.Meta_Information?.feature_total_count ??
      analysis.metadata?.feature_total_count,
  );

  return {
    total_persons_detected,
    total_features_processed,
    feature_total_count: metaCount > 0 ? metaCount : total_features_processed,
  };
}

export function extractFeaturesProcessed(analysisData: unknown): number {
  const analysis = normalizeAnalysisData(analysisData);

  const raw =
    analysis.Meta_Information?.feature_total_count ??
    analysis.metadata?.feature_total_count ??
    analysis.analysis_summary?.total_features ??
    analysis.features?.total_count ??
    analysis.processing?.features_analyzed ??
    analysis.feature_counts?.total ??
    0;

  const fromMeta = parseMetricNumber(raw);
  if (fromMeta > 0) {
    return fromMeta;
  }

  return countFilledAnalysisFields(analysis);
}

const hasFilledSectionValues = (value: unknown): boolean => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  return Object.values(value as Record<string, unknown>).some(
    (entry) =>
      entry !== null &&
      entry !== undefined &&
      entry !== '' &&
      entry !== false &&
      !(Array.isArray(entry) && entry.length === 0),
  );
};

export function countAnalysisCategorySections(analysisData: unknown): number {
  const analysis = normalizeAnalysisData(analysisData);
  return Object.entries(analysis).filter(
    ([key, value]) => !EXCLUDED_TOP_LEVEL_KEYS.has(key) && hasFilledSectionValues(value),
  ).length;
}

export function isSparseAnalysisCache(analysisData: unknown, minFilledFields = 10): boolean {
  return countFilledAnalysisFields(normalizeAnalysisData(analysisData)) < minFilledFields;
}

/** JSONB key on video_analysis_storage.searchable_data; 0..1 scale. */
export function extractOverallQualityScore(searchableData: unknown): number | null {
  let data = searchableData;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      return null;
    }
  }
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return null;
  }

  const raw = (data as Record<string, unknown>).overall_quality_score;
  if (typeof raw === 'number' && !Number.isNaN(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim() !== '') {
    const parsed = parseFloat(raw);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
}

/** Average stored 0..1 scores, scaled to the metrics UI (`x.x/10`). */
export function averageOverallQualityScoreFromStorageRows(
  rows: Array<{ searchable_data?: unknown }>,
): number {
  const scores = rows
    .map((row) => extractOverallQualityScore(row.searchable_data))
    .filter((score): score is number => score !== null);
  if (scores.length === 0) {
    return 0;
  }
  const sum = scores.reduce((acc, score) => acc + score, 0);
  return (sum / scores.length) * 10;
}
